import { HeadingNode, type HeadingTagType, type SerializedHeadingNode } from '@lexical/rich-text';
import type { DOMConversionMap, DOMConversionOutput, ElementFormatType, NodeKey, EditorConfig, LexicalEditor} from 'lexical';
import { $applyNodeReplacement } from 'lexical';

export type SerializedCustomHeadingNode = SerializedHeadingNode & {
	classes: string;
};

export class CustomHeadingNode extends HeadingNode {
	__classes: string;

	static getType(): string {
		return "custom-heading";
	}

	static clone(node: CustomHeadingNode): CustomHeadingNode {
		return new CustomHeadingNode(node.__tag, node.__classes, node.__key);
	}

	constructor(tag: HeadingTagType, classes = '', key?: NodeKey) {
		super(tag, key);
		this.__classes = classes;
	}

	getClasses() {
		return this.__classes;
	}

	setClasses(classes: string) {
		const writable = this.getWritable();
		writable.__classes = classes;
	}

	createDOM(config: EditorConfig) {
		const dom = super.createDOM(config);
		dom.className = this.getClasses();
		return dom;
	}

	exportDOM(editor: LexicalEditor) {
		return super.exportDOM(editor);
	}

	updateDOM(prevNode: CustomHeadingNode, dom: HTMLElement): boolean {
		const classes = this.__classes;

		if (classes !== prevNode.__classes) {
			dom.className = classes;
		}

		return false;
	}

	static importDOM(): DOMConversionMap|null {
		return {
			h1: (node: Node) => ({
				conversion: convertHeadingElement,
				priority: 0,
			}),
			h2: (node: Node) => ({
				conversion: convertHeadingElement,
				priority: 0,
			}),
			h3: (node: Node) => ({
				conversion: convertHeadingElement,
				priority: 0,
			}),
			h4: (node: Node) => ({
				conversion: convertHeadingElement,
				priority: 0,
			}),
			h5: (node: Node) => ({
				conversion: convertHeadingElement,
				priority: 0,
			}),
			h6: (node: Node) => ({
				conversion: convertHeadingElement,
				priority: 0,
			}),
			p: (node: Node) => {
				// domNode is a <p> since we matched it by nodeName
				const paragraph = node as HTMLParagraphElement;
				const firstChild = paragraph.firstChild;
				if (firstChild !== null && isGoogleDocsTitle(firstChild)) {
					return {
						conversion: () => ({node: null}),
						priority: 3,
					};
				}
				return null;
			},
			span: (node: Node) => {
				if (isGoogleDocsTitle(node)) {
					return {
						conversion: (domNode: Node) => {
							return {
								node: $createCustomHeadingNode('h1'),
							};
						},
						priority: 3,
					};
				}
				return null;
			},
		};
	}

	static importJSON(serializedNode: SerializedCustomHeadingNode): CustomHeadingNode {
		const node = $createCustomHeadingNode(serializedNode.tag).updateFromJSON(serializedNode);
		node.setClasses(serializedNode.classes);
		return node;
	}

	exportJSON(): SerializedCustomHeadingNode {
		return {
			...super.exportJSON(),
			type: "custom-heading",
			classes: this.getClasses(),
		};
	}
}

export function $isCustomHeadingNode(node: any) {
	return node instanceof CustomHeadingNode;
}

export function $createCustomHeadingNode(headingTag: HeadingTagType): CustomHeadingNode {
	return $applyNodeReplacement(new CustomHeadingNode(headingTag));
}

function convertHeadingElement(element: HTMLElement): DOMConversionOutput {
	const nodeName = element.nodeName.toLowerCase();
	let node = null;

	if (nodeName === 'h1' || nodeName === 'h2' || nodeName === 'h3' || nodeName === 'h4' || nodeName === 'h5' || nodeName === 'h6') {
		node = $createCustomHeadingNode(nodeName);

		if (element.style !== null) {
			node.setFormat(element.style.textAlign as ElementFormatType);
		}

		if (element.className) {
			node.setClasses(element.className);
		}
	}
	return {node};
}

function isGoogleDocsTitle(domNode: Node): boolean {
	if (domNode.nodeName.toLowerCase() === 'span') {
		return (domNode as HTMLSpanElement).style.fontSize === '26pt';
	}
	return false;
}
